// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
import { useEffect, useMemo, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useCookiePrefAsync, useLoginState } from '#src/app/shared/hooks/session';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import { isLoaded } from '#src/app/shared/utils/async';

import { recordExposure, wasExposedRecently } from './exposureCap';
import { featuredTreats } from './featuredTreats';

type Variation = PsfPromoteOrSharePageData['psfFeaturedSpikingPetitionExperiment']['variation'];

/**
 * The arms the combo page acts on: `v1` and `v2` change the flow, and `fake_control` tracks where the step would
 * have shown. `null` renders production: control, ineligible, or not yet treated.
 */
export type FeaturedSpikingPetitionArm = Extract<Variation, 'v1' | 'v2' | 'fake_control'>;

// The share slide-up's trigger is `md:hidden`. From `md` up the combo Share button is a link to the share
// page, so `v2` could never trigger there, and the population must be the same in every arm.
const SLIDE_UP_VIEWPORT_QUERY = '(min-width: 768px)';

function isSlideUpViewport(): boolean {
	try {
		return !window.matchMedia(SLIDE_UP_VIEWPORT_QUERY).matches;
	} catch {
		return false;
	}
}

function toArm(variation: Variation): FeaturedSpikingPetitionArm | null {
	return variation === 'v1' || variation === 'v2' || variation === 'fake_control' ? variation : null;
}

/**
 * Whether the signer could see every arm's step: the FCM's `enabled` (re-checked here, since
 * `treatExperiment` bypasses `enabledIf`), the login state, and page state Experiments Service can't know.
 * The same in every arm, so treated == exposed == eligible signers.
 */
function useIsEligible(): boolean {
	const {
		data: {
			psfFeaturedSpikingPetitionExperiment: experiment,
			featuredSpikingPetition,
			isShareSlideUpEnabled,
			advocateEntryPointExperiment,
			advocateEntryPointCpDisabled,
			petition: { isCommunityPetition },
		},
	} = usePsfPromoteOrSharePageData();
	const loginState = useLoginState();

	// Same condition as `usePromoteOrShareActions`: the advocate button replaces the share buttons.
	const hidesShareButtons =
		!advocateEntryPointCpDisabled && advocateEntryPointExperiment.variation === 'enabled' && isCommunityPetition;

	return (
		experiment.config.enabled &&
		experiment.experimentName !== '__invalid_experiment__' &&
		!experiment.fallback &&
		!!featuredSpikingPetition &&
		// One-tap sign needs the session user's saved details
		loginState === 'AUTHENTICATED' &&
		isShareSlideUpEnabled &&
		!hidesShareButtons
	);
}

/**
 * psf_featured_spiking_petition_experiment (CHANGE-102055): on combo page load, for an eligible signer in
 * every arm, returns the assigned arm, treats, fires `$exposure`, and records the exposure for the 7-day cap.
 * The cap is written in every arm so repeat signers drop out of all arms alike.
 *
 * A signer without `preferences` consent at treatment is not eligible, in every arm: the cap needs storage, so
 * without consent the step would show after every sign. An arm already applied stays for the page, like a
 * failed treat: the signer is exposed in it. The viewport and the consent are read in the effect, not
 * during render: the viewport is unknown during SSR, and the consent loads after hydration.
 */
export function useFeaturedSpikingPetitionExperiment(): ModelHookResult<{ arm: FeaturedSpikingPetitionArm | null }> {
	const {
		data: { psfFeaturedSpikingPetitionExperiment: experiment },
	} = usePsfPromoteOrSharePageData();
	const utilityContext = useUtilityContext();
	const isEligible = useIsEligible();
	const consentState = useCookiePrefAsync('preferences');
	const [arm, setArm] = useState<FeaturedSpikingPetitionArm | null>(null);

	// `treatExperiment` registers the treatment server-side, but the resolved object stays `treated: false`,
	// and `useTrackExperimentExposure` does nothing while `!treated`. So the exposure object is armed with
	// `treated: true` here. Safe only because the call below fires it on `treatExperiment`'s own `true` return.
	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	useEffect(() => {
		if (!isEligible || !isLoaded(consentState) || !consentState.value || !isSlideUpViewport()) return;
		const now = new Date();
		const { experimentName } = experiment;
		const isTreated = featuredTreats.has(experimentName);

		// The cap applies to a new treat only: a remount after this page load's own exposure (which recorded
		// the cap) keeps the arm.
		if (!isTreated && wasExposedRecently(now)) return;

		// Like the other combo experiments, the arm follows the resolved assignment. A failed treat or
		// `$exposure` is reported, not hidden: a signer without `$exposure` is left out of the analysis in
		// every arm alike.
		setArm(toArm(experiment.variation));

		if (isTreated) return;

		// Recorded whenever the arm applies, not on a successful treat only: an override or a failed treat
		// still sets the arm, so it would otherwise show the step on every visit.
		recordExposure(now);

		// An override forces the arm for QA, but `treatExperiment` would register the session's real
		// assignment, and `useTrackExperimentExposure` never exposes an override. So it is never treated,
		// but it is latched, so a remount keeps the arm past the cap it just recorded.
		if (experiment.override) {
			featuredTreats.set(experimentName, Promise.resolve());
			return;
		}

		featuredTreats.set(
			experimentName,
			(async () => {
				// `treatExperiment` resolves `false`, rather than throwing, when the treatment did not land
				// server-side (network failure, bot session), so `$exposure` is gated on that return.
				try {
					if (!(await treatExperiment(utilityContext, experimentName))) return;
					await trackExposure();
				} catch (error) {
					void utilityContext.errorReporter.report(
						{
							error: new Error('Featured spiking petition experiment treat or $exposure failed', { cause: error }),
							context: { feature: 'psf_featured_spiking_petition_experiment' },
						},
						{ severity: 'warning' },
					);
				}
			})(),
		);
	}, [isEligible, consentState, experiment, utilityContext, trackExposure]);

	return { data: { arm } };
}
